/**
 * OSPROD-267: transcription-pipeline recovery counters.
 *
 * useTranscription's recovery paths (chunk-flow watchdog, track-swap
 * re-arm, visibility restart, server `transcript:session-dead` re-arm,
 * history backfill) used to be console-only, so "transcription froze"
 * reports were untriageable after the fact. These counters ride the
 * existing voice_diag pipeline: voiceTelemetry.collectSample appends
 * them to rawStats as `{ type: 'transcription-diag', v: ... }` (zero CH
 * schema change — same pattern as mic-toggle-fail from OSPROD-248), so
 * the next report is answerable from SQL.
 *
 * Best-effort module-level state — never throws, never affects the call.
 */
export interface TranscriptionDiag {
  /** Successful recorder starts (fresh + restarts). */
  starts: number
  /** Recorder restarts by the recovery paths (all reasons). */
  restarts: number
  lastRestartReason: string
  lastRestartAt: number
  /** Restarts triggered by the chunk-flow watchdog specifically. */
  chunkStalls: number
  /** Server-initiated re-arms (`transcript:session-dead`). */
  sessionDead: number
  /** History backfills merged (voice:getTranscripts responses). */
  backfills: number
  /** Lines received in the most recent backfill. */
  lastBackfillCount: number
}

const diag: TranscriptionDiag = {
  starts: 0,
  restarts: 0,
  lastRestartReason: '',
  lastRestartAt: 0,
  chunkStalls: 0,
  sessionDead: 0,
  backfills: 0,
  lastBackfillCount: 0,
}

let touched = false

export function recordTranscriptionStart(): void {
  touched = true
  diag.starts++
}

export function recordTranscriptionRestart(reason: string): void {
  touched = true
  diag.restarts++
  diag.lastRestartReason = reason
  diag.lastRestartAt = Date.now()
  if (reason === 'chunk-stall') diag.chunkStalls++
  if (reason === 'session-dead') diag.sessionDead++
}

export function recordTranscriptionBackfill(count: number): void {
  touched = true
  diag.backfills++
  diag.lastBackfillCount = count
}

/** null until the transcription pipeline has done anything — keeps the
 *  voice_diag sample free of noise for users who never transcribe. */
export function getTranscriptionDiag(): TranscriptionDiag | null {
  return touched ? { ...diag } : null
}
